import * as React from 'react';

import {Activity_Types} from 'nutshell-core/settings-constants';

import {FormField} from 'shells/form/form-field';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {Body} from 'shells/typography/body';

import type {ActivityTypeDefault} from 'nutshell-graphql-types';

import {GraphQLSelectActivityTypes} from '../../core/select/graphql-select-activity-types';
import {useUpdateSetting} from '../../core/use-update-setting';

import {useGetActivityTypeDefaults} from './graphql/hooks/use-get-activity-type-defaults';

import styles from './activity-type-defaults.module.css';

type ActivityTypeDefaultConfig = {
    id: ActivityTypeDefault;
    innerId: string;
    label: string;
    tooltipInfo: string;
    selectedId: string | null | undefined;
    isSaving: boolean;
    isSavingSetter: (arg1: boolean) => void;
    saveToastState: SavedSettingToastState;
    settingName: string;
};

export function ActivityTypeDefaults() {
    const [isSavingDefaultActivityTypeId, setIsSavingDefaultActivityTypeId] = React.useState(false);
    const [isSavingDefaultSchedulerActivityTypeId, setIsSavingDefaultSchedulerActivityTypeId] =
        React.useState(false);
    const [isSavingClickToCallActivityTypeId, setIsSavingClickToCallActivityTypeId] =
        React.useState(false);
    const [
        isSavingClickToCallVoiceMailActivityTypeId,
        setIsSavingClickToCallVoiceMailActivityTypeId,
    ] = React.useState(false);
    const [isSavingVirtualMeetingActivityTypeId, setIsSavingVirtualMeetingActivityTypeId] =
        React.useState(false);

    const {activityTypeDefaults, isLoading, refetch} = useGetActivityTypeDefaults();

    const {toastState: defaultActivitySettingSaveState} = useManageSaveState(
        isSavingDefaultActivityTypeId,
        1000
    );
    const {toastState: defaultSchedulerActivitySettingSaveState} = useManageSaveState(
        isSavingDefaultSchedulerActivityTypeId,
        1000
    );
    const {toastState: defaultClickToCallActivitySettingSaveState} = useManageSaveState(
        isSavingClickToCallActivityTypeId,
        1000
    );
    const {toastState: defaultClickToCallVoiceMailActivitySettingSaveState} = useManageSaveState(
        isSavingClickToCallVoiceMailActivityTypeId,
        1000
    );
    const {toastState: defaultVirtualMeetingActivitySettingSaveState} = useManageSaveState(
        isSavingVirtualMeetingActivityTypeId,
        1000
    );

    const updateSetting = useUpdateSetting();

    const newActivitiesDefault = activityTypeDefaults.find(
        (activityTypeDefault) => activityTypeDefault.defaultType === 'NEW_ACTIVITIES'
    );
    const schedulerMeetingsDefault = activityTypeDefaults.find(
        (activityTypeDefault) => activityTypeDefault.defaultType === 'SCHEDULER_MEETING'
    );
    const clickToCallDefault = activityTypeDefaults.find(
        (activityTypeDefault) => activityTypeDefault.defaultType === 'CLICK_TO_CALL'
    );
    const clickToCallVoiceMailDefault = activityTypeDefaults.find(
        (activityTypeDefault) => activityTypeDefault.defaultType === 'CLICK_TO_CALL_VOICEMAILS'
    );
    const virtualMeetingDefault = activityTypeDefaults.find(
        (activityTypeDefault) => activityTypeDefault.defaultType === 'VIRTUAL_MEETING'
    );

    const defaultsToRender: ActivityTypeDefaultConfig[] = [
        {
            id: 'NEW_ACTIVITIES',
            innerId: 'default-activity-type',
            label: 'New activities',
            tooltipInfo:
                'The activity type that will be selected by default when creating a new activity.',
            selectedId: newActivitiesDefault && newActivitiesDefault.activityType.id,
            isSaving: isSavingDefaultActivityTypeId,
            isSavingSetter: setIsSavingDefaultActivityTypeId,
            saveToastState: defaultActivitySettingSaveState,
            settingName: Activity_Types.DEFAULT_ACTIVITY_TYPE_ID_SETTING,
        },
        {
            id: 'SCHEDULER_MEETING',
            innerId: 'default-activity-type-scheduler',
            label: 'Scheduler meetings',
            tooltipInfo:
                'The activity type that will be selected by default when creating a new scheduler meeting.',
            selectedId: schedulerMeetingsDefault && schedulerMeetingsDefault.activityType.id,
            isSaving: isSavingDefaultSchedulerActivityTypeId,
            isSavingSetter: setIsSavingDefaultSchedulerActivityTypeId,
            saveToastState: defaultSchedulerActivitySettingSaveState,
            settingName: Activity_Types.DEFAULT_SCHEDULER_ACTIVITY_TYPE_ID_SETTING,
        },
        {
            id: 'CLICK_TO_CALL',
            innerId: 'default-activity-type-click-to-call',
            label: 'Click-to-call calls',
            tooltipInfo:
                'The activity type that will be used when activities are created for click-to-call events.',
            selectedId: clickToCallDefault && clickToCallDefault.activityType.id,
            isSaving: isSavingClickToCallActivityTypeId,
            isSavingSetter: setIsSavingClickToCallActivityTypeId,
            saveToastState: defaultClickToCallActivitySettingSaveState,
            settingName: Activity_Types.DEFAULT_CLICK_TO_CALL_ACTIVITY_TYPE_ID_SETTING,
        },
        {
            id: 'CLICK_TO_CALL_VOICEMAILS',
            innerId: 'default-activity-type-click-to-call-voicemail',
            label: 'Click-to-call voicemails',
            tooltipInfo:
                'The activity type that will be used when activities are created for click-to-call voicemails.',
            selectedId: clickToCallVoiceMailDefault && clickToCallVoiceMailDefault.activityType.id,
            isSaving: isSavingClickToCallVoiceMailActivityTypeId,
            isSavingSetter: setIsSavingClickToCallVoiceMailActivityTypeId,
            saveToastState: defaultClickToCallVoiceMailActivitySettingSaveState,
            settingName: Activity_Types.DEFAULT_CLICK_TO_CALL_VOICEMAILS_ACTIVITY_TYPE_ID_SETTING,
        },
        {
            id: 'VIRTUAL_MEETING',
            innerId: 'default-activity-type-virtual-meeting',
            label: 'Virtual meetings',
            tooltipInfo:
                'The activity type that will be selected by default when logging virtual meetings.',
            selectedId: virtualMeetingDefault && virtualMeetingDefault.activityType.id,
            isSaving: isSavingVirtualMeetingActivityTypeId,
            isSavingSetter: setIsSavingVirtualMeetingActivityTypeId,
            saveToastState: defaultVirtualMeetingActivitySettingSaveState,
            settingName: Activity_Types.DEFAULT_VIRTUAL_MEETINGS_ACTIVITY_TYPE_ID_SETTING,
        },
    ];

    return (
        <LegacySetupTableWrapper title='Defaults'>
            <LegacySetupTableContainer overflow='show'>
                <div className={styles.container}>
                    {defaultsToRender.map((defaultToRender, index) => (
                        <FormField
                            key={defaultToRender.id}
                            innerId={defaultToRender.innerId}
                            label={<Body size='small'>{defaultToRender.label}</Body>}
                            saveToastState={defaultToRender.saveToastState}
                            noMargin={true}
                            tooltipInfo={defaultToRender.tooltipInfo}
                            hasWrappingDiv={false}
                        >
                            <GraphQLSelectActivityTypes
                                // @ts-expect-error
                                inputId={defaultToRender.innerId}
                                selectedId={defaultToRender.selectedId}
                                onChange={(value) => {
                                    defaultToRender.isSavingSetter(true);
                                    updateSetting({
                                        settingName: defaultToRender.settingName,
                                        value: value.id,
                                        isGlobal: true,
                                    })
                                        .then(() => {
                                            refetch();
                                        })
                                        .finally(() => {
                                            defaultToRender.isSavingSetter(false);
                                        });
                                }}
                                placeholder='Select an activity type'
                                isLoading={isLoading}
                                autoFocus={index === 0}
                            />
                        </FormField>
                    ))}
                </div>
            </LegacySetupTableContainer>
        </LegacySetupTableWrapper>
    );
}
